import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    notification,
    Row,
    Col,
    Avatar,
    Tag,
    Popconfirm,
    Grid
} from 'antd';
import {
    PlusOutlined,
    FileSyncOutlined,
    SearchOutlined,
    EyeOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { proformaService } from '../../services/proformaService';
import ProformaForm from './ProformaForm';

const { Column } = Table;
const { Search } = Input;

const ProformaList = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const fetchInvoices = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await proformaService.getAllProformaInvoices({
                page,
                limit: pagination.pageSize,
                search
            });

            setInvoices(Array.isArray(response?.data) ? response.data : []);

            setPagination(prev => ({
                ...prev,
                current: response?.pagination?.page || 1,
                total: response?.pagination?.total || 0
            }));
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch proforma invoices'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices();
    }, []);

    const handleTableChange = (pagination) => {
        fetchInvoices(pagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchInvoices(1, value);
    };

    const handleAdd = () => {
        setSelectedInvoice(null);
        setIsDrawerVisible(true);
    };

    const handleView = (id) => {
        navigate(`/proforma/${id}`);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await proformaService.deleteProformaInvoice(id);
            notification.success({
                message: 'Deleted',
                description: 'Proforma Invoice deleted successfully'
            });
            fetchInvoices(pagination.current, searchText);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || 'Failed to delete proforma invoice'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerVisible(false);
        setSelectedInvoice(null);
    };

    const handleSuccess = () => {
        handleDrawerClose();
        fetchInvoices(pagination.current, searchText);
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col xs={24} sm={12}>
                            <Space>
                                <Avatar size={isMobile ? "middle" : "large"} icon={<FileSyncOutlined />} style={{ backgroundColor: '#1890ff' }} />
                                <div>
                                    <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px' }}>Proforma Invoices</h3>
                                    {!isMobile && <p style={{ margin: 0, color: '#8c8c8c' }}>Manage customer proforma invoices</p>}
                                </div>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                            <Space wrap>
                                <Search
                                    placeholder="Search..."
                                    allowClear
                                    enterButton={<SearchOutlined />}
                                    size={isMobile ? "middle" : "large"}
                                    style={{ width: isMobile ? '160px' : '300px' }}
                                    onSearch={handleSearch}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size={isMobile ? "middle" : "large"}
                                    onClick={handleAdd}
                                >
                                    {isMobile ? "Add" : "New Proforma"}
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Table
                        dataSource={invoices}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            ...pagination,
                            size: isMobile ? 'small' : 'middle',
                            showSizeChanger: !isMobile
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                        size={isMobile ? 'small' : 'middle'}
                    >
                        {isMobile && (
                            <Column
                                title="Proforma"
                                key="proformaMobile"
                                render={(record) => (
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 700 }}>{record.invoiceNumber}</div>
                                        <div style={{ fontSize: '12px' }}>{record.receiver?.customerName}</div>
                                        <div style={{ color: '#1890ff', fontWeight: 600 }}>₹{Math.round(record.totalAfterTax || 0)}</div>
                                    </div>
                                )}
                            />
                        )}
                        <Column
                            title="Proforma Number"
                            dataIndex="invoiceNumber"
                            key="invoiceNumber"
                            responsive={['md']}
                            render={(text) => <strong>{text}</strong>}
                        />
                        <Column
                            title="PO Number"
                            dataIndex="poNumber"
                            key="poNumber"
                            responsive={['md']}
                            render={(text) => text || '-'}
                        />
                        <Column
                            title="Date"
                            dataIndex="invoiceDate"
                            key="invoiceDate"
                            responsive={['md']}
                            render={(date) => new Date(date).toLocaleDateString()}
                        />
                        <Column
                            title="Customer"
                            dataIndex={['receiver', 'customerName']}
                            key="customerName"
                            responsive={['md']}
                        />
                        <Column
                            title="Issued By"
                            dataIndex={['company', 'companyName']}
                            key="companyName"
                            responsive={['md']}
                            render={(text) => <Tag color="blue">{text}</Tag>}
                        />
                        <Column
                            title="Contract Amt"
                            dataIndex="contractAmount"
                            key="contractAmount"
                            responsive={['md']}
                            render={(amount) => `₹${Math.round(amount || 0)}`}
                        />
                        <Column
                            title="Total"
                            dataIndex="totalAfterTax"
                            key="totalAfterTax"
                            responsive={['md']}
                            render={(amount) => (
                                <strong style={{ color: '#1890ff' }}>
                                    ₹{Math.round(amount || 0)}
                                </strong>
                            )}
                        />
                        <Column
                            title="Actions"
                            key="actions"
                            fixed={isMobile ? false : "right"}
                            width={isMobile ? 80 : 100}
                            render={(_, record) => (
                                <Space size="middle">
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => handleView(record.id)}
                                    >
                                        View
                                    </Button>
                                    <Popconfirm
                                        title="Delete Proforma"
                                        description="Are you sure you want to delete this proforma? This action cannot be undone."
                                        onConfirm={() => handleDelete(record.id)}
                                        okText="Yes, Delete"
                                        cancelText="No"
                                        okButtonProps={{ danger: true, loading: loading }}
                                    >
                                        <Button
                                            type="link"
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                        >
                                            Delete
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            )}
                        />
                    </Table>
                </Space>
            </Card>

            <Drawer
                title="Create Proforma Invoice"
                width={isMobile ? '100%' : 900}
                onClose={handleDrawerClose}
                open={isDrawerVisible}
                destroyOnClose
            >
                <ProformaForm
                    onSuccess={handleSuccess}
                    onCancel={handleDrawerClose}
                />
            </Drawer>
        </Space>
    );
};

export default ProformaList;
